"use client";

import { ChevronLeft, OpenInNew, ShoppingCart, Visibility } from "@mui/icons-material";
import { Box } from "@mui/material";
import { ReactNode } from "react";

const iconMap: Record<string, ReactNode> = {
    arrow: <ChevronLeft fontSize="inherit" />,
    cart: <ShoppingCart fontSize="inherit" />,
    eye: <Visibility fontSize="inherit" />,
    external: <OpenInNew fontSize="inherit" />,
};

interface ViewportSettings {
    fontSize: string;
    fontWeight: number;
    textAlign: 'right' | 'center' | 'left';
    lineHeight?: string;
    visible?: boolean;
}

interface TextLCSettings {
    label: string | null;
    mobile: ViewportSettings;
    tablet: ViewportSettings;
    desktop: ViewportSettings;
}

interface TextLCData {
    content: string;
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span';
    color: string | null;
    end_icon?: 'none' | 'arrow' | 'cart' | 'eye' | 'external';
}

export default function TextLC({ content }: {
    content: {
        settings: TextLCSettings;
        data: TextLCData;
    };
}) {
    const { settings, data } = content;
    if (!data.content) return null;

    return (
        <Box
            component={data.tag}
            sx={{
                margin: 0,
                color: data.color ?? '#242424',
                display: {
                    xs: settings.mobile.visible === false ? 'none' : 'block',
                    sm2: settings.tablet.visible === false ? 'none' : 'block',
                    lg: settings.desktop.visible === false ? 'none' : 'block',
                },
                fontSize: {
                    xs: `${settings.mobile.fontSize}px`,
                    sm2: `${settings.tablet.fontSize}px`,
                    lg: `${settings.desktop.fontSize}px`,
                },
                fontWeight: {
                    xs: settings.mobile.fontWeight,
                    sm2: settings.tablet.fontWeight,
                    lg: settings.desktop.fontWeight,
                },
                textAlign: {
                    xs: settings.mobile.textAlign,
                    sm2: settings.tablet.textAlign,
                    lg: settings.desktop.textAlign,
                },
                lineHeight: {
                    xs: settings.mobile.lineHeight || undefined,
                    sm2: settings.tablet.lineHeight || undefined,
                    lg: settings.desktop.lineHeight || undefined,
                },
                whiteSpace: 'pre-wrap',
            }}
        >
            {data.content}
            {data.end_icon && data.end_icon !== 'none' && iconMap[data.end_icon] && (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5, verticalAlign: 'middle' }}>
                    {iconMap[data.end_icon]}
                </Box>
            )}
        </Box>
    );
}
